import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, MessageSquare, Download, ArrowUpDown, ArrowUp, ArrowDown, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  in_corso: 'In Corso',
  completata: 'Completata',
  sospesa: 'Sospesa',
  annullata: 'Annullata'
};

// Colorazione stato (stesso stile di "Pratiche Utenze"): badge saturo + riga con
// sfondo tenue e bordo sinistro colorato
const statusConfig = {
  in_corso:   { label: 'In Corso',   badge: 'bg-blue-200 text-blue-800',     row: 'bg-blue-50 border-l-4 border-l-blue-400' },
  completata: { label: 'Completata', badge: 'bg-green-200 text-green-800',    row: 'bg-green-50 border-l-4 border-l-green-400' },
  sospesa:    { label: 'Sospesa',    badge: 'bg-yellow-200 text-yellow-800',  row: 'bg-yellow-50 border-l-4 border-l-yellow-400' },
  annullata:  { label: 'Annullata',  badge: 'bg-slate-200 text-slate-700',    row: 'bg-slate-50 border-l-4 border-l-slate-400' },
};

const consultingTypeLabels = {
  finanziaria: 'Finanziaria',
  legale: 'Legale',
  fiscale: 'Fiscale',
  immigrazione: 'Immigrazione',
  comune: 'Comune',
  acqua: 'Acqua',
  altro: 'Altro'
};

const emptyConsulting = {
  client_id: '',
  consulting_type: 'finanziaria',
  partner: '',
  subject: '',
  price: '',
  description: '',
  date: '',
  closing_date: '',
  status: 'in_corso',
  payment_partner: false,
  payment_client: false,
  notes: ''
};

const Consulting = () => {
  const [consultings, setConsultings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [clientSearch, setClientSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedConsulting, setSelectedConsulting] = useState(null);
  const [editingConsulting, setEditingConsulting] = useState(null);
  const [formData, setFormData] = useState(emptyConsulting);
  const [dateSort, setDateSort] = useState('none');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [consultingsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/consultings`),
        axios.get(`${API}/clients`)
      ]);
      setConsultings(consultingsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      // If consultings endpoint doesn't exist yet, just load clients
      try {
        const clientsRes = await axios.get(`${API}/clients`);
        setClients(clientsRes.data);
      } catch (e) {
        toast.error('Errore nel caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConsulting) {
        await axios.put(`${API}/consultings/${editingConsulting.id}`, formData);
        toast.success('Consulenza aggiornata');
      } else {
        await axios.post(`${API}/consultings`, formData);
        toast.success('Consulenza creata');
      }
      setDialogOpen(false);
      setEditingConsulting(null);
      setFormData(emptyConsulting);
      setClientSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (consulting) => {
    setEditingConsulting(consulting);
    setFormData({
      client_id: consulting.client_id || '',
      consulting_type: consulting.consulting_type || 'finanziaria',
      partner: consulting.partner || '',
      subject: consulting.subject || '',
      price: consulting.price || '',
      description: consulting.description || '',
      date: consulting.date || '',
      closing_date: consulting.closing_date || '',
      status: consulting.status || 'in_corso',
      payment_partner: consulting.payment_partner || false,
      payment_client: consulting.payment_client || false,
      notes: consulting.notes || ''
    });
    setDetailsDialogOpen(false);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa consulenza?')) return;
    try {
      await axios.delete(`${API}/consultings/${id}`);
      toast.success('Consulenza eliminata');
      setDetailsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleRowClick = (consulting) => {
    setSelectedConsulting(consulting);
    setDetailsDialogOpen(true);
  };

  // Aggiornamento diretto (stato / pagamenti) senza aprire il form di modifica
  const patchConsulting = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/consultings/${id}`, patch);
      setConsultings(prev => prev.map(c => (c.id === id ? res.data : c)));
      setSelectedConsulting(prev => (prev && prev.id === id ? res.data : prev));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('it-IT') : '-';
  const formatPrice = (price) => price ? `${parseFloat(price).toFixed(2).replace('.', ',')} €` : '-';

  const getStatusBadge = (status) => {
    const cfg = statusConfig[status] || statusConfig.in_corso;
    return <Badge className={cfg.badge}>{cfg.label}</Badge>;
  };

  // Select stato "colorato" in tabella: trigger trasparente + badge colorato (come Pratiche Utenze)
  const renderStatusSelect = (consulting) => {
    const cfg = statusConfig[consulting.status] || statusConfig.in_corso;
    return (
      <Select value={consulting.status} onValueChange={(v) => patchConsulting(consulting.id, { status: v })}>
        <SelectTrigger className="h-7 w-[130px] border-0 p-0 bg-transparent shadow-none focus:ring-0" data-testid={`consulting-status-${consulting.id}`}>
          <Badge className={`cursor-pointer ${cfg.badge}`}>{cfg.label}</Badge>
        </SelectTrigger>
        <SelectContent>
          {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
        </SelectContent>
      </Select>
    );
  };

  // Pulsante toggle diretto per i pagamenti (partner / cliente finale)
  const renderPayToggle = (consulting, field, testid) => {
    const on = !!consulting[field];
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid={`${testid}-${consulting.id}`}
        className={`h-8 px-2 ${on ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}
        onClick={() => patchConsulting(consulting.id, { [field]: !on })}
        title={on ? 'Segna come NON pagato' : 'Segna come pagato'}
      >
        {on ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
        <span className="ml-1 text-xs font-medium">{on ? 'Sì' : 'No'}</span>
      </Button>
    );
  };

  const toggleDateSort = () => {
    setDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  // Filter clients for search in form
  const filteredClients = clients.filter(client => {
    if (!clientSearch) return true;
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    return fullName.includes(clientSearch.toLowerCase());
  });

  const filteredConsultings = consultings
    .filter(c => {
      const matchesSearch = `${c.client_name || ''} ${c.subject || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchesType = filterType === 'all' || c.consulting_type === filterType;
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      if (dateSort === 'asc') return new Date(a.date || 0) - new Date(b.date || 0);
      if (dateSort === 'desc') return new Date(b.date || 0) - new Date(a.date || 0);
      // default: ultima consulenza inserita in cima
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  // Pagination
  const totalItems = filteredConsultings.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedConsultings = filteredConsultings.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterType, dateSort]);

  const exportToExcel = () => {
    const headers = ['Cliente', 'Tipo', 'Partner', 'Oggetto', 'Prezzo', 'Data', 'Stato', 'Pag. Partner', 'Pag. Cliente', 'Descrizione'];
    const rows = filteredConsultings.map(c => [
      c.client_name || '',
      consultingTypeLabels[c.consulting_type] || c.consulting_type,
      c.partner || '',
      c.subject || '',
      c.price ? parseFloat(c.price).toFixed(2).replace('.', ',') : '',
      formatDate(c.date),
      statusLabels[c.status] || c.status,
      c.payment_partner ? 'Sì' : 'No',
      c.payment_client ? 'Sì' : 'No',
      c.description || ''
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `consulenze_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  const totalInCorso = consultings.filter(c => c.status === 'in_corso').length;
  const totalCompletate = consultings.filter(c => c.status === 'completata').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="consulting-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Consulenze</h1>
          <p className="text-slate-600 mt-1">Gestisci le consulenze ai clienti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToExcel} disabled={consultings.length === 0} data-testid="export-consultings-btn">
            <Download className="w-4 h-4 mr-2" />Esporta Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setEditingConsulting(null); setFormData(emptyConsulting); setClientSearch(''); }} data-testid="add-consulting-btn">
                <Plus className="w-4 h-4 mr-2" />Nuova Consulenza
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingConsulting ? 'Modifica Consulenza' : 'Nuova Consulenza'}</DialogTitle>
                <DialogDescription>Inserisci i dati della consulenza</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Input
                    placeholder="Cerca cliente per nome o cognome..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="mb-2"
                  />
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                    <SelectTrigger data-testid="consulting-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent>
                      {filteredClients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo Consulenza</Label>
                    <Select value={formData.consulting_type} onValueChange={(v) => setFormData({ ...formData, consulting_type: v })}>
                      <SelectTrigger data-testid="consulting-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(consultingTypeLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Partner</Label>
                    <Input value={formData.partner} onChange={(e) => setFormData({ ...formData, partner: e.target.value })} placeholder="Nome partner" data-testid="consulting-partner" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Oggetto</Label>
                    <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Oggetto della consulenza" data-testid="consulting-subject" />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Apertura</Label>
                    <DateInput value={formData.date} onChange={(val) => setFormData({ ...formData, date: val })} data-testid="consulting-date" placeholder="gg/mm/aaaa" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prezzo (€)</Label>
                    <Input type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="0,00" data-testid="consulting-price" />
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger data-testid="consulting-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Chiusura</Label>
                    <DateInput value={formData.closing_date} onChange={(val) => setFormData({ ...formData, closing_date: val })} data-testid="consulting-closing-date" placeholder="gg/mm/aaaa" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Descrizione dettagliata..." data-testid="consulting-description" />
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="consulting-notes" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setClientSearch(''); }}>Annulla</Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="save-consulting-btn">
                    {editingConsulting ? 'Salva Modifiche' : 'Crea Consulenza'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">In Corso</p>
            <p className="text-2xl font-bold text-blue-600">{totalInCorso}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Completate</p>
            <p className="text-2xl font-bold text-emerald-600">{totalCompletate}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale Consulenze</p>
            <p className="text-2xl font-bold text-slate-900">{consultings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per cliente, oggetto..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-consultings" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-consulting-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(consultingTypeLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-consulting-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all' ? 'Nessuna consulenza trovata' : 'Nessuna consulenza registrata'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Oggetto</TableHead>
                    <TableHead>Prezzo</TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleDateSort}
                        className="inline-flex items-center gap-1 hover:text-amber-600 transition-colors font-medium"
                      >
                        Data
                        {dateSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {dateSort === 'asc' && <ArrowUp className="w-4 h-4 text-amber-600" />}
                        {dateSort === 'desc' && <ArrowDown className="w-4 h-4 text-amber-600" />}
                      </button>
                    </TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Pag. Partner</TableHead>
                    <TableHead className="text-center">Pag. Cliente</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedConsultings.map((consulting) => (
                    <TableRow 
                      key={consulting.id}
                      data-testid={`consulting-row-${consulting.id}`}
                      className={`cursor-pointer hover:bg-amber-50 transition-colors ${(statusConfig[consulting.status] || statusConfig.in_corso).row}`}
                      onClick={() => handleRowClick(consulting)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <MessageSquare className="w-5 h-5 text-amber-600" />
                          </div>
                          <span className="font-medium">{consulting.client_name || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>{consultingTypeLabels[consulting.consulting_type] || consulting.consulting_type}</TableCell>
                      <TableCell>{consulting.partner || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate">{consulting.subject || '-'}</TableCell>
                      <TableCell className="font-medium text-amber-600">{formatPrice(consulting.price)}</TableCell>
                      <TableCell>{formatDate(consulting.date)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {renderStatusSelect(consulting)}
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {renderPayToggle(consulting, 'payment_partner', 'pay-partner')}
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {renderPayToggle(consulting, 'payment_client', 'pay-client')}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`consulting-menu-${consulting.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(consulting)}>
                              <Pencil className="w-4 h-4 mr-2" />Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(consulting.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} consulenze
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Precedente
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettagli Consulenza</DialogTitle>
            <DialogDescription>Visualizza i dettagli della consulenza</DialogDescription>
          </DialogHeader>
          {selectedConsulting && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Cliente</p>
                  <p className="font-medium">{selectedConsulting.client_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Tipo Consulenza</p>
                  <p className="font-medium">{consultingTypeLabels[selectedConsulting.consulting_type] || selectedConsulting.consulting_type}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Partner</p>
                  <p className="font-medium">{selectedConsulting.partner || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Oggetto</p>
                  <p className="font-medium">{selectedConsulting.subject || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Prezzo</p>
                  <p className="font-medium text-amber-600">{formatPrice(selectedConsulting.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Data Apertura</p>
                  <p className="font-medium">{formatDate(selectedConsulting.date)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Data Chiusura</p>
                  <p className="font-medium">{selectedConsulting.closing_date ? formatDate(selectedConsulting.closing_date) : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Stato</p>
                  <div className="mt-1">{getStatusBadge(selectedConsulting.status)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Pagamento Partner</p>
                  <div className="mt-1">{renderPayToggle(selectedConsulting, 'payment_partner', 'details-pay-partner')}</div>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pagamento Cliente finale</p>
                  <div className="mt-1">{renderPayToggle(selectedConsulting, 'payment_client', 'details-pay-client')}</div>
                </div>
              </div>
              {selectedConsulting.description && (
                <div>
                  <p className="text-sm text-slate-500">Descrizione</p>
                  <p className="font-medium whitespace-pre-wrap">{selectedConsulting.description}</p>
                </div>
              )}
              {selectedConsulting.notes && (
                <div>
                  <p className="text-sm text-slate-500">Note</p>
                  <p className="font-medium whitespace-pre-wrap">{selectedConsulting.notes}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>Chiudi</Button>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(selectedConsulting.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />Elimina
                </Button>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => handleEdit(selectedConsulting)}
                >
                  <Pencil className="w-4 h-4 mr-2" />Modifica
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Consulting;
